import { Button, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { TrackedButton } from '$/components/common/Button/TrackedButton';
import { Icon } from '$/components/common/Icon';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import {
  HookFormCheckbox,
  type HookFormCheckboxProps,
} from '$/components/core/Form/HookFormCheckbox';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';
import { StyledLink } from '$/components/core/StyledLink';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { migrateUser } from '$/services/usecases/authentication';
import { useFavoriteStore } from '$/stores/useFavoriteStore';
import { useProjectStore } from '$/stores/useProjectStore';
import { isPasswordValid } from '$/utils/regexUtils';

export type MigrationData = {
  email: string;
  password: string;
  newPassword: string;
  acceptedTerms: boolean;
};

const MigrationInput = (props: HookFormInputProps<MigrationData>) => (
  <HookFormInput<MigrationData> {...props} />
);

const MigrationCheckbox = (props: HookFormCheckboxProps<MigrationData>) => (
  <HookFormCheckbox<MigrationData> {...props} />
);

export const SapCxMigrationForm = () => {
  const { t } = useTranslation();

  const formMethods = useForm<MigrationData>({
    mode: 'onTouched',
    delayError: 100,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const authenticate = useAuthenticationStore.useAuthenticate();
  const closeModal = useAuthenticationStore.useOnModalClose();
  const setAuthModalType = useAuthenticationStore.useSetModalType();
  const migrationPassword = useAuthenticationStore.useMigrationPassword();
  const email = useAuthenticationStore.usePendingAuthEmail();
  const needsPasswordChange = !isPasswordValid(migrationPassword ?? '');

  const { mutate: onMigrate, isPending } = useMutation({
    mutationFn: migrateUser,
    onSuccess: async (data) => {
      const { response, isSuccessful } = data;

      if (
        isSuccessful &&
        response.user != null &&
        response.accessToken != null
      ) {
        authenticate(response.user, response.accessToken);
        await useProjectStore.getState().refetchProjects();
        await useFavoriteStore.getState().getFavorites();
        closeModal();
      } else if (response.message === 'emailNotVerified') {
        setAuthModalType('registerOtp');
      } else {
        setError(cloudMessageTranslation(response.message));
      }
    },
    onError: () => {
      setError(cloudMessageTranslation('genericError'));
    },
  });

  return (
    <FormProvider {...formMethods}>
      <VStack
        as='form'
        gap='10'
        onSubmit={formMethods.handleSubmit((data) =>
          onMigrate({
            ...data,
            password: migrationPassword!,
            email,
            ...(!needsPasswordChange && { newPassword: migrationPassword! }),
          }),
        )}
      >
        <Stack gap='6'>
          <Text fontSize='sm'>
            <Trans i18nKey='dashboard.authentication.sapCxMigration.modalBody' />
          </Text>
          {needsPasswordChange && (
            <MigrationInput
              type={showPassword ? 'text' : 'password'}
              accessor='newPassword'
              label={t('dashboard.authentication.insertPasswordLabel')}
              placeholder={t('dashboard.authentication.defaultPlaceholder', {
                field: t('dashboard.authentication.passwordLabel'),
              })}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
                validate: {
                  passwordValid: (v) =>
                    (needsPasswordChange && isPasswordValid(String(v))) ||
                    t(
                      'dashboard.authentication.errorMessages.passwordRulesMismatch',
                    ),
                },
              }}
              inputRightElement={
                <Button
                  onClick={() => setShowPassword((state) => !state)}
                  variant='text'
                >
                  <Icon
                    icon={showPassword ? 'visibility_on' : 'visibility_off'}
                  />
                </Button>
              }
              description={t(
                // ToDo: update textual password rules
                'dashboard.authentication.errorMessages.passwordRules',
              )}
            />
          )}
          {error && (
            <Text color='form.errorText' fontWeight='bold'>
              {error}
            </Text>
          )}
          <HStack alignItems='flex-start' gap='3'>
            <MigrationCheckbox
              accessor='acceptedTerms'
              registerOptions={{ validate: (val) => val === true }}
              customLabelContent={
                <Text mt='-1' fontSize='xs'>
                  <Trans
                    i18nKey='dashboard.authentication.createAccountLabel'
                    components={{
                      TermsLink: (
                        <StyledLink
                          color='primaryButton.background'
                          textDecoration='underline'
                          to='/tos'
                        >
                          Link
                        </StyledLink>
                      ),
                    }}
                  />
                </Text>
              }
            />
          </HStack>
          <Text fontSize='xs'>
            <Trans
              i18nKey='dashboard.authentication.createAccountLabelPrivacy'
              components={{
                PrivacyLink: (
                  <StyledLink
                    color='primaryButton.background'
                    textDecoration='underline'
                    to='/privacy'
                  >
                    Link
                  </StyledLink>
                ),
              }}
            />
          </Text>
        </Stack>

        <Stack gap='6' w='full'>
          <TrackedButton
            fontSize='sm'
            isDisabled={!formMethods.formState.isValid}
            isLoading={isPending}
            type='submit'
            // ToDo: Tracking?
            // contentName='CloudAccount'
            // contentPiece='Registration'
            // contentTarget='NewAccount'
          >
            {t('general.accept')}
          </TrackedButton>
        </Stack>
      </VStack>
    </FormProvider>
  );
};
